/**
 * ActionHistoryTab — the tool calls the agent made while preparing a
 * section's context, grouped into two collapsed-by-default lists:
 *
 *   🌐 Internet calls      — search_literature (web / literature search)
 *   📚 Local library calls — find_section / find_table / search_library /
 *                            find_image (retrieval over uploaded sources)
 *
 * Each group header is a button; clicking it expands the list of calls.
 * Each call row is itself expandable to reveal detail:
 *   - internet → literature reference cards (title + link, source, year,
 *     PMID/NCT/DOI, abstract snippet), with a query/total header. Runs
 *     drafted before structured capture existed fall back to query + sources.
 *   - library  → the tool params, cascade pass, and the retrieved-body
 *     snippet joined from ProvenanceSourceDoc.
 *
 * Data: getSectionActionHistory(runId, moduleId) — persisted per module run
 * (ModuleRun.tool_calls). Runs recorded before tool-call capture existed
 * return available:false → friendly empty state. Fetch-on-open with the
 * DraftTab cancellation-guard pattern; no SSE.
 */

import { Spinner, makeStyles, mergeClasses } from "@fluentui/react-components";
import React, { useEffect, useRef, useState } from "react";
import {
  type ActionHistoryPayload,
  type AttemptInfo,
  type InternetCall,
  type LibraryCall,
  type OtherCall,
  type TimelineEvent,
  type WebResult,
  getSectionActionHistory,
} from "../../../api/client";
import {
  type ActionTreeNode,
  type ActivityEvent,
  buildActionTree,
} from "@shared/client/actionHistory";
import { describeToolCall } from "../../../config/toolDisplayNames";
import AssistantMarkdown from "../AssistantMarkdown";
import type { SpineSection } from "../../hooks/useVariantCRun";
import { fonts, inkColors, kindColors, statusColors, surfaceColors, typeScale } from "../../tokens";

interface ActionHistoryTabProps {
  section: SpineSection;
  runId: string;
  /** Bumped by the shell when this section's history may have changed (a
   *  lifecycle write, regen, resolve section, or run end) — re-fetches without
   *  the writer switching sections. The mount fetch is the effect's own; this
   *  is an extra trigger that does NOT reset the open/closed tree state. */
  refreshSignal?: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Friendly label + link base for a literature source. */
const SOURCE_META: Record<string, { label: string }> = {
  pubmed: { label: "PubMed" },
  clinical_trials: { label: "ClinicalTrials.gov" },
  biorxiv: { label: "bioRxiv" },
  medrxiv: { label: "medRxiv" },
  chembl: { label: "ChEMBL" },
};

function sourceLabel(source: string): string {
  return SOURCE_META[source]?.label ?? source;
}

/** Human-friendly duration: "340ms" / "1.2s". */
function fmtDuration(ms: number | null | undefined): string | null {
  if (ms == null || ms < 0) return null;
  return ms < 1000 ? `${Math.round(ms)}ms` : `${(ms / 1000).toFixed(1)}s`;
}

/** Compact token count: 1234 → "1.2k". */
function fmtTokens(n: number | null | undefined): string {
  if (!n) return "0";
  return n < 1000 ? String(n) : `${(n / 1000).toFixed(1)}k`;
}

/** Best available external link for a result. */
function resultUrl(r: WebResult): string | null {
  if (r.url) return r.url;
  if (r.pmid) return `https://pubmed.ncbi.nlm.nih.gov/${r.pmid}/`;
  if (r.nct_id) return `https://clinicaltrials.gov/study/${r.nct_id}`;
  if (r.doi) return `https://doi.org/${r.doi}`;
  return null;
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles({
  root: {
    padding: "12px 10px",
    overflowY: "auto",
    height: "100%",
  },
  group: {
    marginBottom: "10px",
    borderTop: `1px solid ${surfaceColors.line}`,
    borderRight: `1px solid ${surfaceColors.line}`,
    borderBottom: `1px solid ${surfaceColors.line}`,
    borderLeft: `1px solid ${surfaceColors.line}`,
    borderRadius: "8px",
    overflow: "hidden",
  },
  groupHeader: {
    display: "flex",
    alignItems: "center",
    columnGap: "8px",
    width: "100%",
    padding: "10px 12px",
    background: surfaceColors.paperDim,
    border: "none",
    cursor: "pointer",
    textAlign: "left",
    fontFamily: fonts.body,
    fontSize: typeScale.body,
    color: inkColors.ink,
    ":hover": { background: surfaceColors.line },
    ":disabled": { cursor: "default", color: inkColors.ink4, opacity: 0.7 },
  },
  groupAccent: {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    flexShrink: 0,
  },
  groupTitle: { fontWeight: 700 },
  groupCount: {
    marginLeft: "auto",
    fontSize: typeScale.code,
    color: inkColors.ink3,
    fontFamily: fonts.mono,
  },
  chevron: {
    fontSize: "10px",
    color: inkColors.ink4,
    width: "12px",
    flexShrink: 0,
    textAlign: "center",
  },
  rowIcon: { flexShrink: 0 },
  callList: {
    padding: "4px 8px 8px",
  },
  callRow: {
    borderTopWidth: "1px",
    borderTopStyle: "solid",
    borderTopColor: surfaceColors.line,
  },
  callButton: {
    display: "flex",
    alignItems: "center",
    columnGap: "8px",
    width: "100%",
    padding: "8px 4px",
    background: "transparent",
    border: "none",
    cursor: "pointer",
    textAlign: "left",
    fontFamily: fonts.body,
    fontSize: typeScale.body,
    color: inkColors.ink2,
    ":hover": { color: inkColors.ink },
  },
  callLabel: {
    flex: 1,
    minWidth: 0,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  outcomeBadge: {
    fontSize: "10px",
    fontFamily: fonts.body,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.4px",
    padding: "1px 7px",
    borderRadius: "8px",
    flexShrink: 0,
    whiteSpace: "nowrap",
  },
  outcomeOk: { color: statusColors.cool, background: "rgba(38, 223, 155, 0.12)" },
  outcomePartial: { color: statusColors.warn, background: "rgba(245, 165, 36, 0.14)" },
  outcomeFail: { color: statusColors.danger, background: "rgba(225, 29, 72, 0.10)" },
  detail: {
    padding: "4px 4px 12px 12px",
    borderLeftWidth: "2px",
    borderLeftStyle: "solid",
    borderLeftColor: surfaceColors.lineStrong,
    marginLeft: "4px",
  },
  detailMeta: {
    fontSize: typeScale.code,
    color: inkColors.ink3,
    fontFamily: fonts.body,
    marginBottom: "8px",
  },
  card: {
    padding: "8px 10px",
    marginBottom: "8px",
    background: surfaceColors.paperDim,
    borderRadius: "6px",
  },
  cardTitle: {
    fontFamily: fonts.body,
    fontSize: typeScale.body,
    fontWeight: 600,
    color: inkColors.ink,
    lineHeight: 1.35,
  },
  cardLink: {
    color: kindColors.web,
    textDecoration: "none",
    ":hover": { textDecoration: "underline" },
  },
  cardMeta: {
    display: "flex",
    flexWrap: "wrap",
    columnGap: "8px",
    rowGap: "2px",
    marginTop: "4px",
    fontSize: typeScale.code,
    fontFamily: fonts.mono,
    color: inkColors.ink3,
  },
  sourceBadge: {
    fontSize: "10px",
    fontFamily: fonts.body,
    fontWeight: 700,
    padding: "1px 6px",
    borderRadius: "6px",
    color: kindColors.web,
    background: "rgba(6, 182, 212, 0.12)",
    whiteSpace: "nowrap",
  },
  cardAbstract: {
    marginTop: "6px",
    fontSize: typeScale.code,
    fontFamily: fonts.body,
    color: inkColors.ink2,
    lineHeight: 1.4,
  },
  snippet: {
    // Chrome only — typography and line structure are governed by the shared
    // assistantMarkdownRules inside AssistantMarkdown (so tables render as tables).
    color: inkColors.ink2,
    background: surfaceColors.paperDim,
    padding: "8px 10px",
    borderRadius: "6px",
  },
  emptyDetail: {
    fontSize: typeScale.code,
    color: inkColors.ink4,
    fontFamily: fonts.body,
    fontStyle: "italic",
  },
  centered: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    rowGap: "8px",
    padding: "32px 16px",
    color: inkColors.ink3,
    fontSize: typeScale.body,
    fontFamily: fonts.body,
    textAlign: "center",
  },
  errorText: { color: statusColors.danger },
  // -- agent tree --
  treeRow: {
    display: "flex",
    alignItems: "center",
    columnGap: "6px",
    width: "100%",
    padding: "6px 4px",
    background: "transparent",
    border: "none",
    cursor: "pointer",
    textAlign: "left",
    fontFamily: fonts.body,
    fontSize: typeScale.body,
    color: inkColors.ink,
    ":hover": { background: surfaceColors.paperDim },
    ":disabled": { cursor: "default", opacity: 0.75 },
  },
  treeChildren: {
    borderLeftWidth: "1px",
    borderLeftStyle: "solid",
    borderLeftColor: surfaceColors.line,
    marginLeft: "9px",
    paddingLeft: "8px",
  },
  treeRole: {
    flex: 1,
    minWidth: 0,
    fontWeight: 700,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  treeSub: {
    flexShrink: 1,
    minWidth: 0,
    fontSize: typeScale.code,
    color: inkColors.ink3,
    fontFamily: fonts.mono,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  treeTarget: { color: inkColors.ink3, fontFamily: fonts.mono, fontSize: typeScale.code },
  // The user's chat instruction, shown as the first item inside an Edit row.
  noteCard: {
    padding: "8px 10px",
    marginTop: "2px",
    background: surfaceColors.paperDim,
    borderRadius: "6px",
  },
  noteCaption: {
    fontSize: typeScale.meta,
    fontFamily: fonts.body,
    fontWeight: 700,
    color: inkColors.ink4,
    textTransform: "uppercase",
    letterSpacing: "0.4px",
    marginBottom: "4px",
  },
  noteText: {
    fontSize: typeScale.body,
    fontFamily: fonts.body,
    color: inkColors.ink2,
    whiteSpace: "pre-wrap",
    lineHeight: "1.5",
    overflowWrap: "anywhere",
  },
  noteSub: {
    marginTop: "4px",
    fontSize: typeScale.code,
    fontFamily: fonts.body,
    color: inkColors.ink3,
    overflowWrap: "anywhere",
  },
  layerLabel: {
    fontSize: typeScale.code,
    fontFamily: fonts.mono,
    color: inkColors.ink4,
    textTransform: "uppercase",
    letterSpacing: "0.4px",
    padding: "6px 4px 2px",
  },
  toolLabel: {
    flex: 1,
    minWidth: 0,
    color: inkColors.ink2,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  actorChip: {
    fontSize: typeScale.code,
    fontFamily: fonts.body,
    fontWeight: 600,
    color: inkColors.ink3,
    background: surfaceColors.paperDim,
    borderRadius: "3px",
    padding: "0 5px",
    flexShrink: 0,
  },
  synthChip: {
    fontSize: "10px",
    fontFamily: fonts.body,
    fontWeight: 700,
    padding: "1px 6px",
    borderRadius: "6px",
    color: statusColors.warn,
    background: "rgba(245, 165, 36, 0.14)",
    flexShrink: 0,
    whiteSpace: "nowrap",
  },
  // --- unified timeline -----------------------------------------------------
  timeline: { display: "flex", flexDirection: "column", gap: "2px" },
  timelineMeta: {
    fontSize: typeScale.meta,
    fontFamily: fonts.mono,
    color: inkColors.ink4,
    flexShrink: 0,
    whiteSpace: "nowrap",
  },
  countBadge: {
    fontSize: "10px",
    fontFamily: fonts.body,
    fontWeight: 700,
    padding: "1px 6px",
    borderRadius: "8px",
    color: inkColors.ink3,
    background: surfaceColors.paperDim,
    flexShrink: 0,
    whiteSpace: "nowrap",
  },
  attemptsBar: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "6px",
    padding: "6px 8px",
    borderBottom: `1px solid ${surfaceColors.paperDim}`,
  },
  attemptsLabel: {
    fontSize: typeScale.meta,
    fontFamily: fonts.body,
    fontWeight: 700,
    color: inkColors.ink4,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  attemptChip: {
    fontSize: "11px",
    fontFamily: fonts.body,
    padding: "2px 8px",
    borderRadius: "10px",
    border: `1px solid ${surfaceColors.paperDim}`,
    background: "transparent",
    color: inkColors.ink2,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  attemptChipActive: {
    background: surfaceColors.paperDim,
    color: inkColors.ink,
    fontWeight: 700,
  },
  reasoningRow: { borderLeft: `2px solid ${statusColors.warn}` },
  reasoningTrace: {
    // Chrome only — typography governed by assistantMarkdownRules (AssistantMarkdown).
    color: inkColors.ink2,
    padding: "8px 10px",
    marginTop: "4px",
    background: surfaceColors.paperDim,
    borderRadius: "6px",
  },
  draftRow: { borderLeft: `2px solid ${statusColors.cool}` },
  tokenPill: {
    fontSize: "10px",
    fontFamily: fonts.mono,
    color: inkColors.ink3,
    padding: "1px 6px",
    borderRadius: "6px",
    background: surfaceColors.paperDim,
    whiteSpace: "nowrap",
  },
});

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const OutcomeBadge: React.FC<{ outcome: string }> = ({ outcome }) => {
  const styles = useStyles();
  // Success is the expected default; a green "SUCCESS" pill on every call is
  // noise. Only surface outcomes that need attention.
  if (outcome === "success" || outcome === "applied") return null;
  const cls = mergeClasses(
    styles.outcomeBadge,
    outcome === "partial" && styles.outcomePartial,
    (outcome === "failure" || outcome === "written_off") && styles.outcomeFail,
  );
  return <span className={cls}>{outcome === "written_off" ? "written off" : outcome}</span>;
};

const LiteratureCard: React.FC<{ result: WebResult }> = ({ result }) => {
  const styles = useStyles();
  const url = resultUrl(result);
  const ids = [
    result.pmid ? `PMID ${result.pmid}` : null,
    result.nct_id ? result.nct_id : null,
    result.doi ? `DOI ${result.doi}` : null,
  ].filter(Boolean) as string[];
  return (
    <div className={styles.card}>
      <div className={styles.cardTitle}>
        {url ? (
          <a className={styles.cardLink} href={url} target="_blank" rel="noopener noreferrer">
            {result.title || "(untitled)"}
          </a>
        ) : (
          result.title || "(untitled)"
        )}
      </div>
      <div className={styles.cardMeta}>
        <span className={styles.sourceBadge}>{sourceLabel(result.source)}</span>
        {result.year != null && <span>{result.year}</span>}
        {result.journal && <span>{result.journal}</span>}
        {ids.map((id) => (
          <span key={id}>{id}</span>
        ))}
      </div>
      {result.abstract && <AssistantMarkdown text={result.abstract} className={styles.cardAbstract} />}
    </div>
  );
};

const InternetDetail: React.FC<{ call: InternetCall }> = ({ call }) => {
  const styles = useStyles();
  const sources = call.web_sources.length ? call.web_sources.map(sourceLabel).join(", ") : "—";
  return (
    <div className={styles.detail}>
      <div className={styles.detailMeta}>
        Query: “{call.query || "—"}”
        {call.web_total != null ? ` · ${call.web_total} result${call.web_total === 1 ? "" : "s"}` : ""}
        {` · sources: ${sources}`}
      </div>
      {call.web_results.length > 0 ? (
        call.web_results.map((r, i) => <LiteratureCard key={`${r.pmid ?? r.nct_id ?? r.doi ?? r.title}-${i}`} result={r} />)
      ) : (
        <div className={styles.emptyDetail}>
          No structured results were captured for this call
          {call.result_length > 0 ? " (drafted before result capture existed)." : "."}
        </div>
      )}
    </div>
  );
};

const LibraryDetail: React.FC<{ call: LibraryCall }> = ({ call }) => {
  const styles = useStyles();
  const params = call.input_params || {};
  const paramBits = Object.entries(params)
    .filter(([, v]) => v != null && String(v).trim() !== "")
    .map(([k, v]) => `${k}: ${String(v).slice(0, 120)}`);
  const detail = call.source_detail;
  const refBits = detail
    ? Object.entries(detail.source_ref || {})
        .filter(([, v]) => v != null && String(v).trim() !== "")
        .map(([k, v]) => `${k}: ${String(v)}`)
    : [];
  return (
    <div className={styles.detail}>
      <div className={styles.detailMeta}>
        {paramBits.length ? paramBits.join(" · ") : "no parameters"}
        {call.cascade_pass != null ? ` · cascade pass ${call.cascade_pass}` : ""}
        {call.result_length ? ` · ${call.result_length.toLocaleString()} chars` : ""}
        {detail?.section_number ? ` · §${detail.section_number}` : ""}
        {refBits.length ? ` · ${refBits.join(" · ")}` : ""}
      </div>
      {detail && detail.snippet ? (
        <AssistantMarkdown text={detail.snippet} className={styles.snippet} />
      ) : (
        <div className={styles.emptyDetail}>No retrieved body was stored for this call.</div>
      )}
    </div>
  );
};

// A non-retrieval tool call (terminal submit_* or any other tool) — shows the
// call's parameters + outcome. The body is deliberately not stored (submit_draft
// carries the whole section), so this shows params, not a result snippet.
const ToolDetail: React.FC<{ call: OtherCall }> = ({ call }) => {
  const styles = useStyles();
  const params = call.input_params || {};
  const paramBits = Object.entries(params)
    .filter(([, v]) => v != null && String(v).trim() !== "")
    .map(([k, v]) => `${k}: ${String(v).slice(0, 200)}`);
  return (
    <div className={styles.detail}>
      <div className={styles.detailMeta}>
        {paramBits.length ? paramBits.join(" · ") : "no parameters"}
        {call.result_length ? ` · ${call.result_length.toLocaleString()} chars` : ""}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Group — collapsed-by-default list of calls
// ---------------------------------------------------------------------------

interface CallGroupProps {
  accent: string;
  icon: string;
  title: string;
  count: number;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

const CallGroup: React.FC<CallGroupProps> = ({ accent, icon, title, count, open, onToggle, children }) => {
  const styles = useStyles();
  const disabled = count === 0;
  return (
    <div className={styles.group}>
      <button
        type="button"
        className={styles.groupHeader}
        onClick={disabled ? undefined : onToggle}
        aria-expanded={open}
        disabled={disabled}
      >
        <span className={styles.groupAccent} style={{ background: accent }} aria-hidden />
        <span aria-hidden>{icon}</span>
        <span className={styles.groupTitle}>{title}</span>
        <span className={styles.groupCount}>{count}</span>
        {!disabled && <span className={styles.chevron}>{open ? "▼" : "▶"}</span>}
      </button>
      {open && !disabled && <div className={styles.callList}>{children}</div>}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Agent tree — superintendent hierarchy (lead planner + drafting children)
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Hierarchical timeline — the agent tree (Superintendent / Drafting agent /
// Resolve pass) with tool-call leaves, reasoning + draft, and attributable
// activity rows (who approved / saved / resolved). Built by the shared
// buildActionTree so the add-in and the web app render identically.
// ---------------------------------------------------------------------------

const ACTOR_TYPE_CLASS: Record<string, string | undefined> = {};

/** A single tree node — recursive. Branches (agent/layer/activity with children)
 *  expand; leaves render their existing detail card on expand. */
const TreeNodeView: React.FC<{
  node: ActionTreeNode;
  openKeys: Set<string>;
  toggle: (k: string) => void;
}> = ({ node, openKeys, toggle }) => {
  const styles = useStyles();
  const open = openKeys.has(node.key);

  // Leaf — a tool call, reasoning, or draft.
  if (node.kind === "leaf" && node.event) {
    const ev = node.event;
    const expandable = ev.kind !== "draft";
    const meta =
      ev.kind === "retrieval" || ev.kind === "internet" || ev.kind === "tool"
        ? fmtDuration(ev.total_duration_ms ?? ev.duration_ms)
        : ev.kind === "reasoning"
          ? `${fmtTokens(ev.thinking_tokens)} tokens`
          : ev.kind === "draft"
            ? fmtDuration(ev.duration_ms)
            : null;
    const rowCls =
      ev.kind === "reasoning" ? styles.reasoningRow : ev.kind === "draft" ? styles.draftRow : undefined;
    // Tool leaves expand only when there are params worth showing.
    const toolHasDetail = ev.kind === "tool" && Object.keys(ev.input_params ?? {}).length > 0;
    const canExpand =
      expandable &&
      (ev.kind !== "reasoning" || (ev.traces_available && ev.traces.length > 0)) &&
      (ev.kind !== "tool" || toolHasDetail);
    return (
      <div className={mergeClasses(styles.callRow, rowCls)}>
        <button
          type="button"
          className={styles.callButton}
          onClick={canExpand ? () => toggle(node.key) : undefined}
          aria-expanded={canExpand ? open : undefined}
          disabled={!canExpand}
        >
          <span className={styles.chevron}>{canExpand ? (open ? "▼" : "▶") : "·"}</span>
          <span className={styles.rowIcon} aria-hidden>{node.icon}</span>
          <span className={styles.callLabel}>{node.label}</span>
          {(ev.kind === "retrieval" || ev.kind === "internet" || ev.kind === "tool") && ev.count > 1 && (
            <span className={styles.countBadge}>×{ev.count}</span>
          )}
          {(ev.kind === "retrieval" || ev.kind === "internet" || ev.kind === "tool") && (
            <OutcomeBadge outcome={ev.outcome} />
          )}
          {meta && <span className={styles.timelineMeta}>{meta}</span>}
        </button>
        {open && ev.kind === "internet" && <InternetDetail call={ev as InternetCall} />}
        {open && ev.kind === "retrieval" && <LibraryDetail call={ev as LibraryCall} />}
        {open && ev.kind === "tool" && <ToolDetail call={ev} />}
        {open && ev.kind === "reasoning" && (
          <div className={styles.detail}>
            {ev.traces.map((t, i) => (
              <AssistantMarkdown key={i} text={t} className={styles.reasoningTrace} />
            ))}
          </div>
        )}
      </div>
    );
  }

  // Activity — an attributable act, possibly with adopted tool-call children.
  if (node.kind === "activity" && node.event && node.event.kind === "activity") {
    const act = node.event as ActivityEvent;
    const hasChildren = node.children.length > 0;
    return (
      <div>
        <button
          type="button"
          className={styles.treeRow}
          onClick={hasChildren ? () => toggle(node.key) : undefined}
          aria-expanded={hasChildren ? open : undefined}
          disabled={!hasChildren}
        >
          <span className={styles.chevron}>{hasChildren ? (open ? "▼" : "▶") : "·"}</span>
          <span className={styles.rowIcon} aria-hidden>{node.icon}</span>
          <span className={styles.treeRole}>{node.label}</span>
          <span className={mergeClasses(styles.actorChip, ACTOR_TYPE_CLASS[node.actorType ?? ""])}>
            {act.actor_type === "ai_agent" ? "Asthra" : act.actor_type}
          </span>
          <OutcomeBadge outcome={act.outcome} />
          {node.sub && <span className={styles.treeSub}>{node.sub}</span>}
        </button>
        {open && hasChildren && (
          <div className={styles.treeChildren}>
            {node.children.map((c) => (
              <TreeNodeView key={c.key} node={c} openKeys={openKeys} toggle={toggle} />
            ))}
          </div>
        )}
      </div>
    );
  }

  // Layer — a plain label whose child nodes are always shown (a dependency
  // band, not a collapsible group), matching the drafting tree's layout.
  if (node.kind === "layer") {
    return (
      <div>
        <div className={styles.layerLabel}>{node.label}</div>
        {node.children.map((c) => (
          <TreeNodeView key={c.key} node={c} openKeys={openKeys} toggle={toggle} />
        ))}
      </div>
    );
  }

  // Note — the user's chat instruction (and the text they highlighted) that
  // heads an Edit's subtree. Plain wrapping text, not a control.
  if (node.kind === "note") {
    return (
      <div className={styles.noteCard}>
        <div className={styles.noteCaption}>Instruction</div>
        <div className={styles.noteText}>{node.label}</div>
        {node.sub && <div className={styles.noteSub}>{node.sub}</div>}
      </div>
    );
  }

  // Branch (agent) — collapsible container.
  const disabled = node.children.length === 0;
  return (
    <div>
      <button
        type="button"
        className={styles.treeRow}
        onClick={disabled ? undefined : () => toggle(node.key)}
        aria-expanded={disabled ? undefined : open}
        disabled={disabled}
      >
        <span className={styles.chevron}>{disabled ? "·" : open ? "▼" : "▶"}</span>
        {node.icon && <span className={styles.rowIcon} aria-hidden>{node.icon}</span>}
        <span className={styles.treeRole}>{node.label}</span>
        {node.sub && <span className={styles.treeSub}>{node.sub}</span>}
      </button>
      {open && !disabled && (
        <div className={styles.treeChildren}>
          {node.children.map((c) => (
            <TreeNodeView key={c.key} node={c} openKeys={openKeys} toggle={toggle} />
          ))}
        </div>
      )}
    </div>
  );
};

/** Default-open keys: the first agent root and the most recent activity node. */
function defaultOpenKeys(roots: ActionTreeNode[]): Set<string> {
  const open = new Set<string>();
  const firstAgent = roots.find((n) => n.kind === "agent");
  if (firstAgent) open.add(firstAgent.key);
  const activities = roots.filter((n) => n.kind === "activity");
  const last = activities[activities.length - 1];
  if (last) open.add(last.key);
  return open;
}

const HierarchicalTimeline: React.FC<{
  payload: ActionHistoryPayload;
  openKeys: Set<string>;
  toggle: (k: string) => void;
}> = ({ payload, openKeys, toggle }) => {
  const styles = useStyles();
  const roots = React.useMemo(() => buildActionTree(payload), [payload]);
  // The terminal draft's cost footnote, when present anywhere in the tree.
  const draft = (payload.timeline ?? []).find((e) => e.kind === "draft") as
    | Extract<TimelineEvent, { kind: "draft" }>
    | undefined;
  return (
    <div className={styles.root}>
      {roots.map((n) => (
        <TreeNodeView key={n.key} node={n} openKeys={openKeys} toggle={toggle} />
      ))}
      {draft && (
        <div className={styles.detailMeta} style={{ padding: "6px 4px 2px" }}>
          <span className={styles.tokenPill}>{draft.model}</span> {fmtTokens(draft.input_tokens)} in ·{" "}
          {fmtTokens(draft.output_tokens)} out
          {draft.thinking_tokens ? ` · ${fmtTokens(draft.thinking_tokens)} reasoning` : ""}
          {draft.draft_length ? ` · ${draft.draft_length.toLocaleString()} chars` : ""}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const ActionHistoryTab: React.FC<ActionHistoryTabProps> = ({ section, runId, refreshSignal }) => {
  const styles = useStyles();
  const [payload, setPayload] = useState<ActionHistoryPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notDrafted, setNotDrafted] = useState(false);
  // Independently collapsible groups; individually expandable call rows.
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());
  const [openCalls, setOpenCalls] = useState<Set<string>>(new Set());
  // Open/closed nodes of the hierarchical timeline, keyed by ActionTreeNode.key
  // (stable across refetches). Seeded once per section with the default-open set.
  const [treeOpen, setTreeOpen] = useState<Set<string>>(new Set());
  // Which prior attempt to show (null = the live/current attempt). Reset when
  // the section changes; driving a re-fetch with ?attempt=N.
  const [selectedAttempt, setSelectedAttempt] = useState<number | null>(null);
  const treeSeededRef = useRef(false);
  const requestKeyRef = useRef<string>("");

  // A different section is a different log — drop any archived-attempt selection.
  useEffect(() => {
    setSelectedAttempt(null);
  }, [section.id]);

  useEffect(() => {
    let cancelled = false;
    const key = `${runId}:${section.id}`;
    requestKeyRef.current = key;
    setLoading(true);
    setError(null);
    setNotDrafted(false);
    setPayload(null);
    setOpenGroups(new Set());
    setOpenCalls(new Set());
    setTreeOpen(new Set());
    treeSeededRef.current = false;
    (async () => {
      try {
        const resp = await getSectionActionHistory(runId, section.id, selectedAttempt ?? undefined);
        if (cancelled || requestKeyRef.current !== key) return;
        setPayload(resp);
      } catch (err) {
        if (cancelled || requestKeyRef.current !== key) return;
        // A 404 means this section has no log yet — it simply hasn't been
        // drafted. That's an expected empty state, not a failure, so route it
        // to the calm "unavailable" message rather than the red error banner.
        if ((err as { status?: number }).status === 404) {
          setNotDrafted(true);
        } else {
          setError(err instanceof Error ? err.message : String(err));
        }
      } finally {
        if (!cancelled && requestKeyRef.current === key) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [runId, section.id, selectedAttempt]);

  // Re-fetch when the shell signals a change (a resolve section finished, a
  // regen completed, the run ended) WITHOUT resetting the tree's open state.
  // The mount value is skipped — the effect above already fetched.
  const refreshSeenRef = useRef(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: refreshSignal is the external re-fetch trigger; runId/section.id are read fresh.
  useEffect(() => {
    if (!refreshSeenRef.current) {
      refreshSeenRef.current = true;
      return;
    }
    let cancelled = false;
    const key = `${runId}:${section.id}`;
    (async () => {
      try {
        const resp = await getSectionActionHistory(runId, section.id, selectedAttempt ?? undefined);
        if (!cancelled && requestKeyRef.current === key) setPayload(resp);
      } catch {
        // Leave the last-good history on screen; a transient refetch failure
        // must not blank a section's Action History.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshSignal]);

  // Seed the tree's open state once per section (first payload), leaving user
  // toggles alone on later refetches.
  useEffect(() => {
    if (!payload || treeSeededRef.current) return;
    if (payload.timeline && payload.timeline.length > 0) {
      treeSeededRef.current = true;
      setTreeOpen(defaultOpenKeys(buildActionTree(payload)));
    }
  }, [payload]);

  const toggle = (set: Set<string>, k: string): Set<string> => {
    const next = new Set(set);
    if (next.has(k)) next.delete(k);
    else next.add(k);
    return next;
  };

  if (loading) {
    return (
      <div className={styles.centered}>
        <Spinner size="tiny" />
        <span>Loading action history…</span>
      </div>
    );
  }
  if (notDrafted) {
    return (
      <div className={styles.centered}>
        <span>Action history isn’t available yet because this section hasn’t been drafted.</span>
        <span>Draft the section to see every tool call the agent made.</span>
      </div>
    );
  }
  if (error) {
    return (
      <div className={styles.centered}>
        <span className={styles.errorText}>Could not load action history: {error}</span>
      </div>
    );
  }
  if (!payload || !payload.available) {
    return (
      <div className={styles.centered}>
        <span>No action history was recorded for this section.</span>
        <span>
          Sections drafted after this rollout will show every context-preparation tool call the agent made — internet
          searches and local library lookups.
        </span>
      </div>
    );
  }

  // Preferred view: the hierarchical timeline (Superintendent / Drafting agent /
  // Resolve pass → tool calls, plus reasoning, draft, and who-did-what activity
  // rows). Present on runs archived after the timeline shipped; older payloads
  // fall through to the flat two-group list below.
  // Prior-attempt selector (archived regen logs + the live one). Shown when a
  // section has been regenerated at least once, so a superseded attempt's tree
  // stays reachable even though the live log was rebuilt from scratch.
  const attempts: AttemptInfo[] = payload.attempts ?? [];
  const currentNum = payload.attempt ?? attempts.find((a) => a.is_current)?.attempt_number ?? null;
  const activeNum = selectedAttempt ?? currentNum;
  const attemptsBar =
    attempts.length > 1 ? (
      <div className={styles.attemptsBar}>
        <span className={styles.attemptsLabel}>Attempt</span>
        {attempts.map((a) => (
          <button
            key={a.attempt_number}
            type="button"
            className={mergeClasses(styles.attemptChip, activeNum === a.attempt_number && styles.attemptChipActive)}
            onClick={() => setSelectedAttempt(a.is_current ? null : a.attempt_number)}
            title={a.guidance || undefined}
          >
            #{a.attempt_number}
            {a.is_current ? " · current" : ""}
          </button>
        ))}
      </div>
    ) : null;

  if (payload.timeline && payload.timeline.length > 0) {
    return (
      <div className={styles.root}>
        {attemptsBar}
        <HierarchicalTimeline payload={payload} openKeys={treeOpen} toggle={(k) => setTreeOpen((s) => toggle(s, k))} />
      </div>
    );
  }

  return (
    <div className={styles.root}>
      {attemptsBar}
      <CallGroup
        accent={kindColors.web}
        icon="🌐"
        title="Internet calls"
        count={payload.internet_calls.length}
        open={openGroups.has("internet")}
        onToggle={() => setOpenGroups((s) => toggle(s, "internet"))}
      >
        {payload.internet_calls.map((call, i) => {
          const k = `internet-${i}`;
          const open = openCalls.has(k);
          return (
            <div key={k} className={styles.callRow}>
              <button
                type="button"
                className={styles.callButton}
                onClick={() => setOpenCalls((s) => toggle(s, k))}
                aria-expanded={open}
              >
                <span className={styles.chevron}>{open ? "▼" : "▶"}</span>
                <span className={styles.callLabel}>{describeToolCall(call.tool_name, call.input_params, true)}</span>
                <OutcomeBadge outcome={call.outcome} />
              </button>
              {open && <InternetDetail call={call} />}
            </div>
          );
        })}
      </CallGroup>

      <CallGroup
        accent={kindColors.library}
        icon="📚"
        title="Local library calls"
        count={payload.library_calls.length}
        open={openGroups.has("library")}
        onToggle={() => setOpenGroups((s) => toggle(s, "library"))}
      >
        {payload.library_calls.map((call, i) => {
          const k = `library-${i}`;
          const open = openCalls.has(k);
          return (
            <div key={k} className={styles.callRow}>
              <button
                type="button"
                className={styles.callButton}
                onClick={() => setOpenCalls((s) => toggle(s, k))}
                aria-expanded={open}
              >
                <span className={styles.chevron}>{open ? "▼" : "▶"}</span>
                <span className={styles.callLabel}>{describeToolCall(call.tool_name, call.input_params, true)}</span>
                <OutcomeBadge outcome={call.outcome} />
              </button>
              {open && <LibraryDetail call={call} />}
            </div>
          );
        })}
      </CallGroup>
    </div>
  );
};

export default ActionHistoryTab;
